import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
import { format } from 'date-fns';
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { AlertCircle, Flag } from 'lucide-react';

const STATUS_COLORS = {
  green: '#10b981',
  yellow: '#f59e0b',
  red: '#ef4444',
};

export default function KRProgressChart({ activity, progressReports }) {
  const keyResults = activity?.key_results || [];
  
  // Max 2 KRs selected
  const [selectedKRs, setSelectedKRs] = useState(() => {
    const initial = keyResults.slice(0, 2).map(kr => kr.description);
    return initial;
  });
  
  const [showStatusChanges, setShowStatusChanges] = useState(true);
  const [showIssues, setShowIssues] = useState(true);

  const toggleKR = (krDesc) => {
    setSelectedKRs(prev => {
      if (prev.includes(krDesc)) {
        return prev.filter(k => k !== krDesc);
      }
      if (prev.length >= 2) {
        // Replace the oldest selection
        return [prev[1], krDesc];
      }
      return [...prev, krDesc];
    });
  };

  // Build chart data from APPROVED progress reports only (sorted oldest first for chart)
  const chartData = useMemo(() => {
    const approvedReports = progressReports.filter(r => r.approved);
    const sorted = [...approvedReports].sort((a, b) => new Date(a.reported_at) - new Date(b.reported_at));
    
    let lastStatus = null;
    
    return sorted.map((report, idx) => {
      const proposedStatus = report.proposed_status_flag || 'yellow';
      const dataPoint = {
        date: report.reported_at,
        dateLabel: format(new Date(report.reported_at), 'MMM d'),
        status: proposedStatus,
        hasIssue: !!report.issues_description,
        issueText: report.issues_description,
        statusChanged: lastStatus && lastStatus !== proposedStatus,
        prevStatus: lastStatus,
      };

      // Add KR values
      selectedKRs.forEach((krDesc, i) => {
        const krProgress = (report.key_results_progress || []).find(p => p.description === krDesc);
        const value = krProgress ? parseFloat(krProgress.new_value) || 0 : null;
        dataPoint[`kr${i}`] = value;
        dataPoint[`kr${i}Desc`] = krDesc;
      });

      lastStatus = proposedStatus;
      return dataPoint;
    });
  }, [progressReports, selectedKRs]);

  // Get targets for selected KRs
  const krTargets = selectedKRs.map(krDesc => {
    const kr = keyResults.find(k => k.description === krDesc);
    return kr ? parseFloat(kr.target) || 100 : 100;
  });

  // Calculate line colors based on the latest APPROVED KR progress for each selected KR
  const getKRLineColor = (krDesc) => {
    // Get the most recent APPROVED report
    const approvedReports = progressReports.filter(r => r.approved);
    const latestApprovedReport = approvedReports.length > 0 ? 
      [...approvedReports].sort((a, b) => new Date(b.reported_at) - new Date(a.reported_at))[0] : null;
    
    if (!latestApprovedReport) {
      // Fall back to current activity key results
      const kr = keyResults.find(k => k.description === krDesc);
      if (kr) {
        const current = parseFloat(kr.current_value) || 0;
        const target = parseFloat(kr.target) || 1;
        const progress = target > 0 ? (current / target) * 100 : 0;
        return progress >= 70 ? '#10b981' : progress >= 40 ? '#f59e0b' : '#ef4444';
      }
      return '#f59e0b'; // Default amber
    }
    
    // Get progress from latest approved report
    const krProgress = (latestApprovedReport.key_results_progress || []).find(p => p.description === krDesc);
    const kr = keyResults.find(k => k.description === krDesc);
    
    if (krProgress && kr) {
      const current = parseFloat(krProgress.new_value) || 0;
      const target = parseFloat(kr.target) || 1;
      const progress = target > 0 ? (current / target) * 100 : 0;
      return progress >= 70 ? '#10b981' : progress >= 40 ? '#f59e0b' : '#ef4444';
    }
    
    return '#f59e0b'; // Default amber
  };

  const lineColors = selectedKRs.map(krDesc => getKRLineColor(krDesc));

  // Custom dot component to show issues and status changes - larger dots with status flag color
  const CustomDot = (props) => {
    const { cx, cy, payload, dataKey } = props;
    if (cx === undefined || cy === undefined) return null;
    
    const isFirstKR = dataKey === 'kr0';
    const statusColor = STATUS_COLORS[payload.status] || STATUS_COLORS.yellow;
    
    return (
      <g>
        {/* Base dot - larger size (r=7) with status flag color */}
        <circle cx={cx} cy={cy} r={7} fill={statusColor} stroke="#0f172a" strokeWidth={2} />
        
        {/* Issue indicator */}
        {showIssues && payload.hasIssue && isFirstKR && (
          <g transform={`translate(${cx - 6}, ${cy - 22})`}>
            <circle cx={6} cy={6} r={9} fill="#ef4444" />
            <text x={6} y={10} textAnchor="middle" fill="white" fontSize={12} fontWeight="bold">!</text>
          </g>
        )}
        
        {/* Status change indicator */}
        {showStatusChanges && payload.statusChanged && isFirstKR && (
          <g transform={`translate(${cx + 10}, ${cy - 10})`}>
            <rect x={-2} y={-2} width={16} height={16} rx={3} fill={statusColor} stroke="#0f172a" strokeWidth={1} />
            <path d="M6 2 L6 10 L2 6" fill="none" stroke="white" strokeWidth={1.5} transform="translate(1,2)" />
          </g>
        )}
      </g>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload[0]) return null;
    const data = payload[0].payload;
    
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-xl text-xs">
        <div className="font-bold text-slate-200 mb-2">{format(new Date(data.date), 'MMM d, yyyy HH:mm')}</div>
        
        {selectedKRs.map((kr, i) => (
          <div key={i} className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: lineColors[i] }} />
            <span className="text-slate-400 truncate max-w-[150px]">{kr}:</span>
            <span className="font-mono text-white font-bold">{data[`kr${i}`]}</span>
            <span className="text-slate-500">/ {krTargets[i]}</span>
          </div>
        ))}
        
        <div className="mt-2 pt-2 border-t border-slate-700">
          <Badge className={cn(
            "text-[9px] h-4",
            data.status === 'green' ? 'bg-emerald-600' :
            data.status === 'yellow' ? 'bg-amber-500 text-black' : 'bg-rose-600'
          )}>
            Status: {data.status}
          </Badge>
          
          {data.statusChanged && (
            <span className="ml-2 text-[10px] text-slate-400">
              (changed from {data.prevStatus})
            </span>
          )}
        </div>
        
        {data.hasIssue && (
          <div className="mt-2 pt-2 border-t border-slate-700 flex items-start gap-1 text-rose-400">
            <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
            <span className="line-clamp-2">{data.issueText}</span>
          </div>
        )}
      </div>
    );
  };

  const approvedCount = progressReports.filter(r => r.approved).length;
  
  if (approvedCount < 2) {
    return (
      <div className="text-center py-6 text-slate-500 text-sm italic">
        Need at least 2 approved progress reports to show chart.
      </div>
    );
  }

  if (keyResults.length === 0) {
    return (
      <div className="text-center py-6 text-slate-500 text-sm italic">
        No key results defined for this activity.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Chart */}
      <div className="h-48 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 5 }}>
            <XAxis 
              dataKey="dateLabel" 
              tick={{ fill: '#64748b', fontSize: 10 }}
              axisLine={{ stroke: '#334155' }}
              tickLine={{ stroke: '#334155' }}
            />
            
            {/* Left Y-axis for first KR */}
            <YAxis 
              yAxisId="left"
              tick={{ fill: '#64748b', fontSize: 10 }}
              axisLine={{ stroke: '#334155' }}
              tickLine={{ stroke: '#334155' }}
              domain={[0, krTargets[0] * 1.1]}
              width={35}
            />
            
            {/* Right Y-axis for second KR (if selected) */}
            {selectedKRs.length > 1 && (
              <YAxis 
                yAxisId="right"
                orientation="right"
                tick={{ fill: '#64748b', fontSize: 10 }}
                axisLine={{ stroke: '#334155' }}
                tickLine={{ stroke: '#334155' }}
                domain={[0, krTargets[1] * 1.1]}
                width={35}
              />
            )}
            
            <Tooltip content={<CustomTooltip />} />
            
            {/* First KR Line */}
            {selectedKRs[0] && (
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="kr0"
                stroke={lineColors[0]}
                strokeWidth={2}
                dot={<CustomDot />}
                activeDot={{ r: 6, fill: lineColors[0], stroke: '#0f172a', strokeWidth: 2 }}
                connectNulls
              />
            )}
            
            {/* Second KR Line */}
            {selectedKRs[1] && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="kr1"
                stroke={lineColors[1]}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={<CustomDot />}
                activeDot={{ r: 6, fill: lineColors[1], stroke: '#0f172a', strokeWidth: 2 }}
                connectNulls
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-1 px-1 bg-slate-900/50 rounded-lg p-1.5 border border-slate-800/50">
        {/* KR Selectors */}
        {keyResults.map((kr, idx) => {
          const isSelected = selectedKRs.includes(kr.description);
          const selectionIndex = selectedKRs.indexOf(kr.description);
          return (
            <button
              key={idx}
              onClick={() => toggleKR(kr.description)}
              className={cn(
                "text-[10px] px-2.5 py-1.5 rounded transition-all max-w-[100px] truncate font-medium",
                isSelected 
                  ? selectionIndex === 0 
                    ? "bg-indigo-600 text-white shadow-md" 
                    : "bg-indigo-600/70 text-white shadow-md border border-dashed border-indigo-400"
                  : "text-slate-500 hover:text-slate-300 hover:bg-slate-800"
              )}
              title={kr.description}
            >
              {kr.description}
            </button>
          );
        })}

        {/* Divider */}
        <div className="w-px h-5 bg-slate-700 mx-1" />

        {/* Issue toggle */}
        <button
          onClick={() => setShowIssues(!showIssues)}
          className={cn(
            "flex items-center gap-1.5 text-[10px] px-2.5 py-1.5 rounded transition-all font-medium",
            showIssues 
              ? "bg-rose-600/20 text-rose-400 border border-rose-500/30" 
              : "text-slate-500 hover:text-slate-300 hover:bg-slate-800"
          )}
        >
          <AlertCircle className="w-3 h-3" />
          Issues
        </button>

        {/* Status toggle */}
        <button
          onClick={() => setShowStatusChanges(!showStatusChanges)}
          className={cn(
            "flex items-center gap-1.5 text-[10px] px-2.5 py-1.5 rounded transition-all font-medium",
            showStatusChanges 
              ? "bg-amber-600/20 text-amber-400 border border-amber-500/30" 
              : "text-slate-500 hover:text-slate-300 hover:bg-slate-800"
          )}
        >
          <Flag className="w-3 h-3" />
          Status
        </button>
      </div>
    </div>
  );
}